
import Vue from "vue";
import { mapState, mapMutations } from "vuex";
import { setFieldToNULL, updateTable, getAllPaginateItems } from "@/api";
import PopoverMenu from "@/components/PopoverMenu";
import UpdateMultipleItems from "../components/UpdateMultipleItems";
import { headersInterface } from "@/interfaces/Tables.interfaces";
import { constants } from "@/utils/constants";
import { startCase } from "lodash";
import { currentTimeZone, formatNotes, getHeaders } from "@/helpers";
import {
  getFfsStatusColor,
  getFfsStatusLabel,
  getFfsStatusIcon,
  ffsStatusNeedsDarkText,
  resolveCanonicalFfsPresentationCode
} from "@/helpers/ffsStatus";
import { format } from "date-fns";
import { utcToZonedTime } from "date-fns-tz";
import { getDataTables } from "@/mixins/getDataTables";
import {
  formatAdminEnum,
  USER_ACCOUNT_STATUS_LABELS
} from "@/helpers/adminEnumLabels";

export default Vue.extend({
  name: "Hygienist",
  components: {
    PopoverMenu,
    UpdateMultipleItems
  },
  mixins: [getDataTables],
  data: () => {
    return {
      modalHeader: "",
      tableSearch: "",
      showModal: false,
      action: () => {},
      currentItem: { id: 0, column: "", value: 0 },
      options: {} as any,
      countryCode: constants.countryCode,
      hygienistTypeCode: constants.hygienistType,
      previousStripeAccountId: null as string | null,
      allHygienists: [] as Array<Record<string, any>>,
      fullDatasetLoaded: false
    };
  },
  computed: {
    ...mapState({
      tablename: "tablename",
      headers: "tableHeaders",
      content: "tableContent",
      preSaveData: "preparedDataToSave"
    }),
    tableHeaders(): Array<headersInterface> {
      const tableColumns = constants.enabledColumns.hygienistTable;
      const data = tableColumns.map(i => {
        return { align: "left", text: startCase(i), value: i };
      });
      return data;
    }
  },
  methods: {
    ...mapMutations(["modify", "modifyMultiple", "callSnackbar"]),
    formatAccountStatus(value: number | string | null | undefined): string {
      return formatAdminEnum(USER_ACCOUNT_STATUS_LABELS, value);
    },
    async getDataFromApi(forceRefresh = false) {
      const vm = this as any;

      if (vm.loading) {
        return;
      }

      if (forceRefresh) {
        vm.fullDatasetLoaded = false;
      }

      vm.loading = true;
      try {
        const itemsPerPage = vm.options.itemsPerPage || 15;
        const requestedPage = vm.options.page || 1;
        const { sortBy = [], sortDesc = [] } = vm.options;

        if (!vm.fullDatasetLoaded) {
          const columnKey = vm.tablename.toLowerCase() + "Table";
          const columnNames =
            constants.enabledColumns[columnKey] ||
            constants.enabledColumns["hygienistTable"];

          vm.allHygienists = await getAllPaginateItems(
            {
              tableName: vm.tablename,
              limit: 1000,
              page: 1,
              columnNames
            },
            { maxPages: 200 }
          );
          vm.fullDatasetLoaded = true;
        }

        const searchTerm = vm.tableSearch.trim().toLowerCase();
        const searchColumns = constants.enabledColumns[
          vm.tablename.toLowerCase() + "TableSearch"
        ] || [
          "id",
          "firstName",
          "lastName",
          "phone",
          "city",
          "province",
          "postalCode",
          "CDHONumber",
          "school"
        ];

        let workingSet = [...vm.allHygienists];

        if (searchTerm.length) {
          workingSet = workingSet.filter(item =>
            searchColumns.some(column => {
              const value = item[column];
              return (
                value !== null &&
                typeof value !== "undefined" &&
                value
                  .toString()
                  .toLowerCase()
                  .includes(searchTerm)
              );
            })
          );
        }

        if (sortBy.length) {
          const sortKey = sortBy[0];
          const descending = !!sortDesc[0];
          workingSet.sort((a, b) => {
            const valueA = a[sortKey];
            const valueB = b[sortKey];

            if (valueA === valueB) return 0;
            if (valueA === null || typeof valueA === "undefined") return 1;
            if (valueB === null || typeof valueB === "undefined") return -1;

            if (valueA < valueB) return descending ? 1 : -1;
            if (valueA > valueB) return descending ? -1 : 1;
            return 0;
          });
        }

        const totalItems = workingSet.length;
        const maxPage = Math.max(Math.ceil(totalItems / itemsPerPage), 1);
        const currentPage = Math.min(requestedPage, maxPage);
        if (currentPage !== requestedPage) {
          vm.options.page = currentPage;
        }

        const start = (currentPage - 1) * itemsPerPage;
        const pageItems = workingSet.slice(start, start + itemsPerPage);
        const headers = workingSet.length > 0 ? getHeaders(workingSet) : [];

        vm.modifyMultiple([
          { property: "tableHeaders", value: headers },
          { property: "tableContent", value: pageItems }
        ]);

        vm.totalItems = totalItems;
      } catch (error) {
        (this as any).callSnackbar({
          visible: true,
          text: "Error loading hygienists",
          color: "red darken-2"
        });
      } finally {
        vm.loading = false;
      }
    },
    findHeaderIndex(name: string): number {
      return this.headers.findIndex(item => item.value === name);
    },
    async saveStripeAccountId(item) {
      try {
        console.log("💳 Saving Stripe Account ID:", {
          id: item.id,
          newValue: item.stripeAccountId,
          previousValue: this.previousStripeAccountId
        });

        await updateTable({
          tablename: this.tablename,
          columnname: "stripeAccountId",
          id: item.id,
          value: item.stripeAccountId || null,
          prevValue: this.previousStripeAccountId
        });

        // Refresh the table data
        await (this as any).getDataFromApi(true);

        (this as any).callSnackbar({
          visible: true,
          text: "Stripe Account ID saved successfully",
          color: "green darken-2"
        });
      } catch (error) {
        console.error("❌ Error saving Stripe Account ID:", error);
        (this as any).callSnackbar({
          visible: true,
          text: "Error saving Stripe Account ID",
          color: "red darken-2"
        });
      }
    },
    async modalSubmit() {
      this.action();
      this.modalHeader = "";
      this.showModal = false;
    },
    clickSetStripeAccToNull() {
      this.modalHeader = "Set this ID to Null?";
      this.showModal = true;
      this.action = this.setSpripeAccToNull;
    },
    async setSpripeAccToNull() {
      await Promise.all([
        setFieldToNULL({
          tablename: this.tablename,
          columnname: "stripeAccountId",
          id: this.currentItem.id
        }),
        setFieldToNULL({
          tablename: this.tablename,
          columnname: "stripeCreatedTimestamp",
          id: this.currentItem.id
        })
      ]);
      await (this as any).getDataFromApi(true);
    },
    toggleBoolean(id: number, column: string, value: number | boolean) {
      this.currentItem = {
        id,
        column,
        value: value === 1 || value === true ? 1 : 0
      };
      this.action = this.updateBooleanField;
      this.showModal = true;
      const columnNames = {
        WeeklyNewsletter: "Weekly Newsletter",
        PriorityNotifications: "Priority Notifications",
        receivePostedShifts: "Receive Posted Shifts"
      };
      const currentValue = value === 1 || value === true;
      const columnLabel =
        columnNames[column as keyof typeof columnNames] || column;
      this.modalHeader = currentValue
        ? `Turn off ${columnLabel}?`
        : `Turn on ${columnLabel}?`;
    },
    updateBooleanField() {
      const changedData = this.currentItem.value === 0 ? 1 : 0;
      const data = this.content.map((item: any) => {
        if (item.id === this.currentItem.id) {
          item[this.currentItem.column] = changedData;
        }
        return item;
      });

      this.allHygienists = this.allHygienists.map(item => {
        if (item.id === this.currentItem.id) {
          return {
            ...item,
            [this.currentItem.column]: changedData
          };
        }
        return item;
      });

      this.modifyMultiple([
        {
          property: "preparedDataToSave",
          value: [
            ...this.preSaveData,
            {
              tablename: this.tablename,
              column: this.currentItem.column,
              columnId: this.currentItem.id,
              prevValue: this.currentItem.value,
              value: changedData
            }
          ]
        },
        { property: "tableContent", value: data }
      ]);
    },
    formatNotes(note: string | null) {
      return formatNotes(note);
    },
    formatDateTime(UTCdateTime: string) {
      return format(
        utcToZonedTime(UTCdateTime, currentTimeZone()),
        "MMM d yyyy, HH:mm"
      );
    },

    // Resolve status presentation from canonical membership fields only.
    // T2-6516: an active recovery overlay (course_required / restricted_reentry /
    // on_a_break / not_a_fit), enriched onto the row by the Talent list endpoint,
    // takes precedence over the durable tier so support sees the recovery state.
    rowStatusCode(item: any): string {
      return resolveCanonicalFfsPresentationCode(
        item?.currentStatusCode,
        item?.activeOverlayState
      ) || "";
    },
    getStatusColor(code: string): string {
      return getFfsStatusColor(code);
    },
    getStatusIcon(code: string): string | null {
      return getFfsStatusIcon(code);
    },
    getStatusLabel(code: string): string {
      return getFfsStatusLabel(code);
    },
    statusNeedsDarkText(code: string): boolean {
      return ffsStatusNeedsDarkText(code);
    }
  }
});
